import {
  MatchState,
  RoundResult,
  RoundState,
} from '@alienheadwars/football-card-manager-model';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { SpeedDurations } from '../../../../MotionMui/MotionMui';
import { TutorialContext } from '../../../../base/tutorial/tutorialTracker/TutorialTracker';
import { usePopupOnOverlayContext } from '../../../../base/overlay/PopupOnOverlay';

export enum MatchViewStatesIndex {
  SET_BALL_ZONE,
  SHOW_ATTACKING_PLAYER,
  SHOW_DEFENDING_PLAYER,
  SHOW_ATTACKING_TARGET_PLAYER,
  FIRST_CONTEST_SCORES,
  FIRST_CONTEST_LOSERS_DISCARD,
  SECOND_CONTEST_BALL_ZONE,
  SECOND_CONTEST_DEFENDER,
  SECOND_CONTEST_SCORES,
  SECOND_CONTEST_LOSERS_DISCARD,
  VICTORS_DISCARD,
  UPDATE_SCORE,
  //State only exists to delay state change for a further second if a goal was scored
  GOAL_BASK,
  FINISHED,
}

const getNextMatchViewStatesIndex = (index: MatchViewStatesIndex) => {
  return index === MatchViewStatesIndex.FINISHED ? 0 : index + 1;
};

const MatchViewStatesTiming = {
  [MatchViewStatesIndex.SET_BALL_ZONE]: SpeedDurations.Fast,
  [MatchViewStatesIndex.SHOW_ATTACKING_PLAYER]: SpeedDurations.Normal,
  [MatchViewStatesIndex.SHOW_DEFENDING_PLAYER]: SpeedDurations.Normal,
  [MatchViewStatesIndex.SHOW_ATTACKING_TARGET_PLAYER]: SpeedDurations.Normal,
  [MatchViewStatesIndex.FIRST_CONTEST_SCORES]: SpeedDurations.Slow,
  [MatchViewStatesIndex.FIRST_CONTEST_LOSERS_DISCARD]: SpeedDurations.Normal,
  [MatchViewStatesIndex.SECOND_CONTEST_BALL_ZONE]: SpeedDurations.Fast,
  [MatchViewStatesIndex.SECOND_CONTEST_DEFENDER]: SpeedDurations.Normal,
  [MatchViewStatesIndex.SECOND_CONTEST_SCORES]: SpeedDurations.Slow,
  [MatchViewStatesIndex.SECOND_CONTEST_LOSERS_DISCARD]: SpeedDurations.Normal,
  [MatchViewStatesIndex.VICTORS_DISCARD]: SpeedDurations.Normal,
  [MatchViewStatesIndex.UPDATE_SCORE]: SpeedDurations.VeryFast,
  [MatchViewStatesIndex.GOAL_BASK]: SpeedDurations.Slow,
  [MatchViewStatesIndex.FINISHED]: SpeedDurations.VeryFast,
} as const;

interface MatchViewStatesRestriction {
  skipCondition(round: RoundState): boolean;
  readyCondition(round: RoundState): boolean;
}

const noSkipCondition = () => false;
const noReadyCondition = () => true;
const noConditions = {
  skipCondition: noSkipCondition,
  readyCondition: noReadyCondition,
};

const MatchViewStatesRestrictions: {
  [key in MatchViewStatesIndex]: MatchViewStatesRestriction;
} = {
  [MatchViewStatesIndex.SET_BALL_ZONE]: noConditions,
  [MatchViewStatesIndex.SHOW_ATTACKING_PLAYER]: {
    skipCondition: noSkipCondition,
    readyCondition: (roundState) => roundState.attackingPlayer !== undefined,
  },
  [MatchViewStatesIndex.SHOW_DEFENDING_PLAYER]: {
    skipCondition: noSkipCondition,
    readyCondition: (roundState) => roundState.defendingPlayer !== undefined,
  },
  [MatchViewStatesIndex.SHOW_ATTACKING_TARGET_PLAYER]: {
    skipCondition: noSkipCondition,
    readyCondition: (roundState) =>
      roundState.attackingTargetPlayer !== undefined,
  },
  [MatchViewStatesIndex.FIRST_CONTEST_SCORES]: {
    skipCondition: (roundState) => roundState.contests.length < 1,
    readyCondition: (roundState) => !!roundState.roundFinished,
  },
  [MatchViewStatesIndex.FIRST_CONTEST_LOSERS_DISCARD]: {
    skipCondition: (roundState) => roundState.contests.length < 1,
    readyCondition: noReadyCondition,
  },
  [MatchViewStatesIndex.SECOND_CONTEST_BALL_ZONE]: {
    skipCondition: (roundState) => roundState.contests.length < 2,
    readyCondition: noReadyCondition,
  },
  [MatchViewStatesIndex.SECOND_CONTEST_DEFENDER]: {
    skipCondition: (roundState) => roundState.contests.length < 2,
    readyCondition: noReadyCondition,
  },
  [MatchViewStatesIndex.SECOND_CONTEST_SCORES]: {
    skipCondition: (roundState) => roundState.contests.length < 2,
    readyCondition: noReadyCondition,
  },
  [MatchViewStatesIndex.SECOND_CONTEST_LOSERS_DISCARD]: {
    skipCondition: (roundState) => roundState.contests.length < 2,
    readyCondition: noReadyCondition,
  },
  [MatchViewStatesIndex.VICTORS_DISCARD]: noConditions,
  [MatchViewStatesIndex.UPDATE_SCORE]: noConditions,
  //Skip if no goal
  [MatchViewStatesIndex.GOAL_BASK]: {
    skipCondition: (roundState) => roundState.roundResult !== RoundResult.GOAL,
    readyCondition: noReadyCondition,
  },
  [MatchViewStatesIndex.FINISHED]: noConditions,
};

export interface MatchViewStateProps {
  matchState: MatchState;
  children: any;
}

export interface MatchViewState {
  currentRound: RoundState;
  matchViewStatesIndex: MatchViewStatesIndex;
  matchState: MatchState;
  scoreState: MatchState;
  updateCurrentRound(roundState: RoundState): void;
}

const defaulMatchViewStateContext = {
  currentRound: null as any,
  previousRound: null as any,
  matchViewStatesIndex: MatchViewStatesIndex.SET_BALL_ZONE,
  matchState: null as any,
  scoreState: null as any,
  updateCurrentRound: (roundState: RoundState) => {},
};

const MatchViewStateContext = React.createContext<MatchViewState>(
  defaulMatchViewStateContext
);

export const MatchViewStateProvider = MatchViewStateContext.Provider;
export const useMatchViewState = () => React.useContext(MatchViewStateContext);

export interface MatchViewStateProps {
  //TODO, considered having match id passed through and have this state listen for updates
  matchState: MatchState;
  children: any;
}
export const MatchViewContextComponent = (props: MatchViewStateProps) => {
  const [matchViewState, setMatchViewState] = useState({
    matchViewStatesIndex: 0,
    matchViewStatesIndexCompleted: false,
    currentRound: props.matchState.rounds[props.matchState.rounds.length - 1],
    scoreState: props.matchState,
    matchState: props.matchState,
  });

  const [internalMatchViewState, setInternalMatchViewState] = useState({
    matchViewStatesIndex: 0,
    matchViewStatesIndexCompleted: false,
  });

  const tutorialActive = useContext(TutorialContext).currentTutorial;
  const overlayActive = usePopupOnOverlayContext().isActivePopup;

  //At the start of a new round cycle, update the round to the latest
  useEffect(() => {
    setMatchViewState((current) => {
      if (
        internalMatchViewState.matchViewStatesIndex ===
        MatchViewStatesIndex.UPDATE_SCORE
      ) {
        return {
          ...current,
          ...internalMatchViewState,
          scoreState: props.matchState,
          currentRound: {
            ...current.currentRound,
            ...props.matchState.rounds[current.currentRound.roundId],
          },
          matchState: props.matchState,
        };
      } else if (internalMatchViewState.matchViewStatesIndex === 0) {
        return {
          ...current,
          ...internalMatchViewState,
          // scoreState: props.matchState,
          currentRound:
            props.matchState.rounds[props.matchState.rounds.length - 1],
          matchState: props.matchState,
        };
      } else
        return {
          ...current,
          ...internalMatchViewState,
          currentRound: {
            ...current.currentRound,
            ...props.matchState.rounds[current.currentRound.roundId],
          },
          matchState: props.matchState,
        };
    });
  }, [props.matchState, internalMatchViewState]);

  const updateCurrentRound = useCallback(
    (roundState: RoundState) => {
      setMatchViewState((current) => {
        return {
          ...current,
          currentRound: roundState,
        };
      });
    },
    [setMatchViewState]
  );

  //manage matchViewStatesIndex
  useEffect(() => {
    const currentMatchViewStatesIndex =
      internalMatchViewState.matchViewStatesIndex;
    if (
      //If it's the end of the last round, stop processing states
      (currentMatchViewStatesIndex === MatchViewStatesIndex.FINISHED &&
        matchViewState.matchState.matchFinished &&
        matchViewState.currentRound.roundId ===
          matchViewState.matchState.rounds.length - 1) ||
      //Or if either a tutorial or an overlay is active
      overlayActive ||
      tutorialActive
    ) {
      //do not update
      return;
    }
    //If current view has complete
    if (internalMatchViewState.matchViewStatesIndexCompleted) {
      let nextMatchViewStatesIndex: MatchViewStatesIndex =
        getNextMatchViewStatesIndex(currentMatchViewStatesIndex);
      const roundState = matchViewState.currentRound;
      let nextMatchViewStatesIndexIsReady: boolean =
        MatchViewStatesRestrictions[nextMatchViewStatesIndex].readyCondition(
          roundState
        );
      //If round is ready && skippable, iterate to the next one
      while (
        nextMatchViewStatesIndexIsReady &&
        MatchViewStatesRestrictions[nextMatchViewStatesIndex].skipCondition(
          roundState
        )
      ) {
        nextMatchViewStatesIndex = getNextMatchViewStatesIndex(
          nextMatchViewStatesIndex
        );
        nextMatchViewStatesIndexIsReady =
          MatchViewStatesRestrictions[nextMatchViewStatesIndex].readyCondition(
            roundState
          );
      }
      //Check next state is ready
      if (nextMatchViewStatesIndexIsReady) {
        //If it is, update
        setInternalMatchViewState((current) => {
          //We check that the state hasn't already updated whilst we were doing checks due to overlapping runs triggered by concurrent match state updates
          if (current.matchViewStatesIndex === currentMatchViewStatesIndex)
            return {
              matchViewStatesIndex: nextMatchViewStatesIndex,
              matchViewStatesIndexCompleted: false,
            };
          else return current;
        });
      }
    }
    //Otherwise manage current view
    else {
      setTimeout(() => {
        setInternalMatchViewState((current) => {
          //We check that the state hasn't already updated whilst we were doing checks due to overlapping runs triggered by concurrent match state updates
          if (current.matchViewStatesIndex === currentMatchViewStatesIndex)
            return {
              matchViewStatesIndex: currentMatchViewStatesIndex,
              matchViewStatesIndexCompleted: true,
            };
          else return current;
        });
      }, MatchViewStatesTiming[currentMatchViewStatesIndex as MatchViewStatesIndex] * 1000);
    }
  }, [matchViewState, internalMatchViewState, overlayActive, tutorialActive]);

  return (
    <MatchViewStateProvider
      value={{
        ...matchViewState,
        updateCurrentRound,
      }}
    >
      {props.children}
    </MatchViewStateProvider>
  );
};
